import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Phone, ArrowRight, Loader2, ArrowLeft, Store, ChevronRight,
  ShoppingCart, MonitorPlay, Wallet, Users, LifeBuoy, Network,
} from 'lucide-react';

const FEATURES = [
  { title: 'Orders & POs', desc: 'Purchasing, circuits & tracking.', icon: ShoppingCart, color: 'text-amber-400' },
  { title: 'IPTV & OTT', desc: 'Activate, renew & sync subscriptions.', icon: MonitorPlay, color: 'text-fuchsia-400' },
  { title: 'Distributor Wallets', desc: '3-tier wallets & Easebuzz payments.', icon: Wallet, color: 'text-emerald-400' },
  { title: 'LCO Management', desc: 'Reseller mapping & custom pricing.', icon: Users, color: 'text-sky-400' },
  { title: 'Helpdesk', desc: 'Centralized ticketing & support.', icon: LifeBuoy, color: 'text-rose-400' },
  { title: 'IPAM Subnets', desc: 'Advanced IP allocation & network mgmt.', icon: Network, color: 'text-violet-400' },
];

// Ivory input on the olive/gold card
const INPUT_CLS = 'h-11 bg-white border-[#d8cfa8] text-[#3f4a17] placeholder:text-[#a79f78] focus-visible:ring-[#c9a227] focus-visible:border-transparent';

export default function Login() {
  const navigate = useNavigate();
  const { login, API } = useAuth();
  const [loginMethod, setLoginMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setStep('2fa');
        toast.info('OTP sent to your WhatsApp for verification');
      } else {
        toast.success('Login successful');
      }
    } else {
      toast.error(result.error);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('Please enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-2fa`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, otp })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Login successful');
        window.location.href = '/';
      } else {
        toast.error(data.detail || (res.status === 400 ? 'Invalid or expired OTP' : '2FA verification failed'));
      }
    } catch (error) {
      toast.error('Connection failed. Please check your internet and try again.');
    } finally { setLoading(false); }
  };

  const handleSendPhoneOTP = async (e) => {
    e.preventDefault();
    if (!phone || phone.length !== 10) { toast.error('Please enter a valid 10-digit phone number'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/send-employee-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('OTP sent to your WhatsApp!');
        setStep('otp');
      } else {
        toast.error(res.status === 404 ? 'Phone number not registered. Please contact admin.' : (data.detail || 'Failed to send OTP'));
      }
    } catch (error) {
      toast.error('Connection failed. Please check your internet and try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyPhoneOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('Please enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-employee-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Login successful');
        window.location.href = '/';
      } else {
        toast.error(data.detail || (res.status === 400 ? 'Invalid or expired OTP' : 'Verification failed'));
      }
    } catch (error) {
      toast.error('Connection failed. Please check your internet and try again.');
    } finally { setLoading(false); }
  };

  const resetForm = () => { setStep('credentials'); setOtp(''); setTempToken(''); };
  const backToEmail = () => { setLoginMethod('email'); setStep('credentials'); setOtp(''); };

  const GOLD_BTN = 'w-full h-11 bg-gradient-to-r from-[#d4af37] via-[#c9a227] to-[#a88a2a] hover:from-[#c9a227] hover:to-[#8f7422] text-white font-bold shadow-md';

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2"
      style={{ background: 'linear-gradient(135deg,#3f4a17 0%,#5c6524 26%,#8a7d2c 50%,#c9a227 72%,#7d3f9c 100%)' }}
      data-testid="login-page"
    >
      {/* Left: golden flower + portal description */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img
          src="/golden-flower-bg.png" alt="Golden lotus" aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="golden-flower-bg"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(45,53,20,0.82) 0%,rgba(63,74,23,0.6) 50%,rgba(98,45,120,0.4) 100%)' }} />

        <div className="relative z-10">
          <img src="/logo.png" alt="ANT Broadband" className="h-14 w-auto" data-testid="login-logo" />
        </div>

        <div className="relative z-10 max-w-lg">
          <p className="text-xs font-bold tracking-[0.25em] text-[#e9c76b] uppercase">Power of Optic Fiber</p>
          <h1 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight text-white drop-shadow">
            One command center for your entire ISP operation.
          </h1>
          <p className="mt-4 text-base text-[#f3efdf] leading-relaxed">
            Manage orders, distributor wallets, IPTV &amp; OTT subscriptions, resellers and support — all from a single,
            fast and secure portal built for network operators.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-10" data-testid="marketing-features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex flex-col gap-1.5">
                  <Icon className={`h-6 w-6 ${f.color}`} />
                  <p className="text-sm font-semibold text-white">{f.title}</p>
                  <p className="text-sm text-[#dcd6b8]">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <p className="relative z-10 text-xs text-[#d8d0aa]">© {new Date().getFullYear()} ANT Broadband. All rights reserved.</p>
      </div>

      {/* Right: single login */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div
          className="w-full max-w-sm rounded-2xl border border-[#e3dab7] bg-[#fbf8ef] shadow-2xl p-8 space-y-7"
          data-testid="login-card"
        >
          {/* Mobile logo + flower */}
          <div className="lg:hidden flex flex-col items-center gap-3">
            <img src="/logo.png" alt="ANT Broadband" className="h-14 w-auto" />
            <img src="/golden-flower-bg.png" alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#d4af37]" />
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <p className="text-xs font-bold tracking-[0.22em] text-[#a88a2a] uppercase">Staff &amp; Admin</p>
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-[#3f4a17]">Sign in to ANT Broadband</h2>
            <p className="text-sm text-[#7a744f]">Access the order management console.</p>
          </div>

          {/* Email / Password */}
          {loginMethod === 'email' && step === 'credentials' && (
            <div className="space-y-5">
              <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#5a5320]">Email Address</Label>
                  <Input id="email" type="email" placeholder="you@company.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className={INPUT_CLS} data-testid="email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#5a5320]">Password</Label>
                  <Input id="password" type="password" placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)} required className={INPUT_CLS} data-testid="password-input" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-button" className={GOLD_BTN}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Sign In'}
                </Button>
                <div className="text-center">
                  <a href="/forgot-password" className="text-sm text-[#7a744f] hover:text-[#a88a2a] transition-colors">Forgot your password?</a>
                </div>
              </form>

              <div className="relative flex items-center">
                <div className="flex-1 border-t border-[#e3dab7]" />
                <span className="mx-4 text-xs font-medium text-[#8a8256] uppercase tracking-wider">Or</span>
                <div className="flex-1 border-t border-[#e3dab7]" />
              </div>

              <Button type="button" variant="outline" onClick={() => setLoginMethod('phone')} data-testid="login-whatsapp-btn"
                className="w-full h-11 border-[#6b7328] text-[#3f4a17] hover:bg-[#6b7328]/10 hover:text-[#3f4a17]">
                <Phone className="mr-2 h-4 w-4 text-[#6b7328]" /> Login with WhatsApp OTP
              </Button>
            </div>
          )}

          {/* Phone / OTP request */}
          {loginMethod === 'phone' && step === 'credentials' && (
            <form onSubmit={handleSendPhoneOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#5a5320]">WhatsApp Number</Label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-[#efe9d2] border border-[#d8cfa8] rounded-md text-[#5a5320] text-sm">+91</div>
                  <Input id="phone" type="tel" placeholder="9876543210" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className={`flex-1 ${INPUT_CLS}`} data-testid="phone-input" />
                </div>
                <p className="text-xs text-[#8a8256]">We'll send a one-time password to this number.</p>
              </div>
              <Button type="submit" disabled={loading} className={GOLD_BTN}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending OTP...</> : <><Phone className="mr-2 h-4 w-4" /> Send OTP</>}
              </Button>
              <button type="button" onClick={backToEmail} className="w-full text-sm text-[#7a744f] hover:text-[#3f4a17] transition-colors flex items-center justify-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to email login
              </button>
            </form>
          )}

          {/* Phone OTP verify */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyPhoneOTP} className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-[#7a744f]">Enter the OTP sent to</p>
                <p className="font-medium text-[#3f4a17]">+91 {phone}</p>
              </div>
              <Input type="text" placeholder="••••••" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`text-center text-2xl tracking-[0.5em] h-14 ${INPUT_CLS}`} maxLength={6} autoFocus data-testid="otp-input" />
              <Button type="submit" disabled={loading} className={GOLD_BTN}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</> : <><ArrowRight className="mr-2 h-4 w-4" /> Verify & Login</>}
              </Button>
              <button type="button" onClick={resetForm} className="w-full text-sm text-[#7a744f] hover:text-[#3f4a17] transition-colors">Change phone number</button>
            </form>
          )}

          {/* 2FA verify */}
          {step === '2fa' && (
            <form onSubmit={handle2FAVerify} className="space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#6b7328]/12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-6 w-6 text-[#6b7328]" />
                </div>
                <p className="text-sm text-[#7a744f]">Two-factor authentication required</p>
                <p className="text-xs text-[#8a8256] mt-1">Check your WhatsApp for the verification code</p>
              </div>
              <Input type="text" placeholder="••••••" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`text-center text-2xl tracking-[0.5em] h-14 ${INPUT_CLS}`} maxLength={6} autoFocus data-testid="twofa-input" />
              <Button type="submit" disabled={loading} className={GOLD_BTN}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</> : <><ArrowRight className="mr-2 h-4 w-4" /> Verify</>}
              </Button>
              <button type="button" onClick={resetForm} className="w-full text-sm text-[#7a744f] hover:text-[#3f4a17] transition-colors">Back to login</button>
            </form>
          )}

          <div className="relative flex items-center">
            <div className="flex-1 border-t border-[#e3dab7]" />
            <span className="mx-3 text-[11px] font-medium text-[#8a8256] uppercase tracking-wider">Partner / LCO?</span>
            <div className="flex-1 border-t border-[#e3dab7]" />
          </div>

          {/* Partner / LCO login option */}
          <button
            type="button"
            onClick={() => navigate('/partner')}
            data-testid="partner-portal-link"
            className="group relative block w-full text-left rounded-xl border border-[#d9c9e6] bg-gradient-to-r from-[#f3ecfa] to-[#efe7f6] p-4 transition-all hover:border-[#7d3f9c] hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#7d3f9c]/12 flex items-center justify-center group-hover:bg-[#7d3f9c]/22 transition-colors">
                  <Store className="h-5 w-5 text-[#7d3f9c]" />
                </div>
                <div>
                  <p className="text-base font-semibold text-[#4a1f66] group-hover:text-[#7d3f9c] transition-colors">Partner / LCO Login</p>
                  <p className="mt-0.5 text-xs text-[#6a5a78]">Distributors &amp; resellers portal.</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[#9a7fae] group-hover:text-[#7d3f9c] group-hover:translate-x-0.5 transition-all" />
            </div>
          </button>

          <p className="text-center text-xs text-[#a79f78]">Secured with WhatsApp OTP &amp; 2FA.</p>
        </div>
      </div>
    </div>
  );
}
